import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Group, Badge, Anchor, Alert, ThemeIcon, Table, Breadcrumbs, Code } from '@mantine/core';
import { Lightbulb, Warning, Check, Minus } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

export default function NegativeKeywordsGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Wykluczające słowa kluczowe w Google Ads"
        description="Jak używać wykluczających słów kluczowych, aby obniżyć koszty reklam. Analiza search terms, listy wykluczeń i automatyzacja z Droid."
        path={`${guidesBase}/negative-keywords`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Wykluczające słowa kluczowe</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Wykluczające słowa kluczowe</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Wykluczające słowa kluczowe to najszybszy sposób na oszczędności. Blokują wyświetlanie reklam na nieistotne zapytania. Droid automatycznie analizuje search terms i dodaje wykluczenia.</Text>
      </Alert>

      {/* What are negatives */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Czym są wykluczające słowa kluczowe?</Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Jeśli sprzedajesz nowe buty sportowe, nie chcesz wyświetlać reklam na zapytania "buty sportowe używane" lub "buty sportowe za darmo". Wykluczenia blokują takie zapytania.
        </Text>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Słowo kluczowe</Table.Th>
              <Table.Th>Wykluczenie</Table.Th>
              <Table.Th>Zapytanie "buty sportowe używane"</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td><Text size="sm">"buty sportowe"</Text></Table.Td>
              <Table.Td><Text size="sm" c="dimmed">Brak</Text></Table.Td>
              <Table.Td><Badge color="red" size="xs" variant="light">Wyświetla się (marnowanie)</Badge></Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td><Text size="sm">"buty sportowe"</Text></Table.Td>
              <Table.Td><Text size="sm" c="red.6" fw={500}>-używane</Text></Table.Td>
              <Table.Td><Badge color="green" size="xs" variant="light">Zablokowane</Badge></Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Match types for negatives */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Dopasowania wykluczeń</Text>
        <Alert color="yellow" mb="sm" icon={<Warning size={14} />}>
          <Text size="xs">Wykluczenia działają inaczej niż zwykłe słowa kluczowe! Wykluczenie "Broad" blokuje tylko gdy WSZYSTKIE słowa z wykluczenia występują w zapytaniu.</Text>
        </Alert>
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Typ</Table.Th>
              <Table.Th>Przykład wykluczenia</Table.Th>
              <Table.Th>Blokuje</Table.Th>
              <Table.Th>Nie blokuje</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {[
              ['Przybliżone', 'buty używane', '"tanie buty używane sportowe"', '"buty z drugiej ręki"'],
              ['Fraza', '"buty używane"', '"tanie buty używane"', '"używane obuwie sportowe"'],
              ['Dokładne', '[buty używane]', 'tylko "buty używane"', '"tanie buty używane"'],
            ].map(([type, ex, blocks, allows], i) => (
              <Table.Tr key={i}>
                <Table.Td><Text size="sm" fw={500}>{type}</Text></Table.Td>
                <Table.Td><Code>{ex}</Code></Table.Td>
                <Table.Td><Text size="xs" c="green.7">{blocks}</Text></Table.Td>
                <Table.Td><Text size="xs" c="red.6">{allows}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Common negatives */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Popularne wykluczenia wg branży</Text>
        <Stack gap="md">
          {[
            { category: 'Uniwersalne', words: ['darmowe', 'za darmo', 'free', 'jak zrobić', 'DIY', 'poradnik', 'youtube', 'pdf', 'chomikuj', 'torrenty'] },
            { category: 'E-commerce', words: ['używane', 'second hand', 'allegro', 'olx', 'gratka', 'naprawa', 'serwis', 'reklamacja', 'zwrot'] },
            { category: 'Usługi B2B', words: ['praca', 'staż', 'kariera', 'oferta pracy', 'ile zarabia', 'kurs', 'szkolenie', 'studia'] },
            { category: 'Lokalne usługi', words: ['opinie', 'forum', 'ranking', 'porównanie', 'najlepszy', 'najtańszy'] },
          ].map(({ category, words }, i) => (
            <Box key={i}>
              <Text size="sm" fw={500} mb={4}>{category}</Text>
              <Group gap={4}>
                {words.map((w, j) => (
                  <Badge key={j} size="sm" variant="light" color="red" styles={{ root: { textTransform: 'none' } }}>
                    -{w}
                  </Badge>
                ))}
              </Group>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* How Droid helps */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Jak Droid zarządza wykluczeniami</Text>
        <Stack gap="xs">
          {[
            'Automatycznie analizuje raport wyszukiwanych haseł (Search Terms Report)',
            'Identyfikuje frazy z kliknięciami, ale bez konwersji (marnowanie budżetu)',
            'Sugeruje dodanie wykluczeń z poziomu sugestii AI (typ: "Targetowanie")',
            'Po zastosowaniu sugestii, wykluczenia są widoczne w zakładce "Słowa kluczowe" kampanii',
            'Możesz też dodawać wykluczenia ręcznie w Google Ads',
          ].map((tip, i) => (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="brand" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm" lh={1.5}>{tip}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>

      {/* Best practices */}
      <Paper withBorder p="lg" radius="md">
        <Text size="sm" fw={600} mb="sm">Najlepsze praktyki</Text>
        <Stack gap="xs">
          {[
            'Sprawdzaj raport wyszukiwanych haseł co tydzień (lub pozwól Droid robić to automatycznie)',
            'Twórz listy wykluczeń na poziomie konta (współdzielone między kampaniami)',
            'Nie wykluczaj zbyt agresywnie, szczególnie na początku. Zbieraj dane minimum 2 tygodnie',
            'Dla e-commerce: wyklucz nazwy konkurentów (chyba że celowo targetujesz)',
            'Dla usług: wyklucz "praca", "kariera", "ile zarabia" od razu',
          ].map((tip, i) => (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm" lh={1.5}>{tip}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
